import React from 'react';
import { Navigate } from 'react-router-dom';
import { useModules } from '../hooks/useGlobalSettings';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { useUser } from '../contexts/UserContext';
import LoadingScreen from './common/LoadingScreen';

interface ModuleRouteProps {
    module: string;
    children: React.ReactElement;
    fallback?: string;
}

/**
 * Module-protected route component
 * Only allows access if the specified module is enabled
 * Respects both global and tenant-level module activation
 */
const ModuleRoute: React.FC<ModuleRouteProps> = ({ module, children, fallback = '/dashboard' }) => {
    const { isModuleEnabled, loading: globalLoading } = useModules();
    const { isModuleEnabledForTenant, loading: tenantLoading } = useTenantSettings();
    const { isGlobalAdmin } = useUser();

    if (globalLoading || tenantLoading) {
        return <LoadingScreen />;
    }

    // Map module IDs to module names in settings
    const moduleMapping: Record<string, string> = {
        'presentations': 'presentations',
        'consultants': 'consultants',
        'evaluationSchemas': 'evaluationSchemas',
        'todos': 'todo', // Legacy template module
        'documents': 'documents',
        'calendar': 'calendar',
        'billing': 'billing'
    };

    const settingsModuleName = moduleMapping[module] || module;

    // Global admins only need global module enabled
    if (isGlobalAdmin()) {
        if (!isModuleEnabled(settingsModuleName)) {
            return <Navigate to={fallback} replace />;
        }
    } else {
        // All other users (including tenant admins) need tenant-level module enabled
        if (!isModuleEnabledForTenant(settingsModuleName)) {
            return <Navigate to={fallback} replace />;
        }
    }

    return children;
};

export default ModuleRoute;
